import { useEffect, useState } from "react";
import api, { formatIDR, formatApiErrorDetail, formatDateTime } from "@/lib/api";
import { AdminLayout } from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { name: "", description: "", location: "", date: "", end_date: "", poster_url: "", category: "Umum", status: "published", ticket_types: [] };

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => api.get("/admin/events").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const toLocalInput = (s) => (s ? s.replace(" ", "T").slice(0, 16) : "");
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (ev) => {
    setForm({ ...ev, date: toLocalInput(ev.date), end_date: toLocalInput(ev.end_date),
              status: ev.status || "published", ticket_types: ev.ticket_types.map((t) => ({ ...t })) });
    setEditId(ev.id); setOpen(true);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const addTT = () => setForm({ ...form, ticket_types: [...form.ticket_types, { name: "", price: 0, quota: 0 }] });
  const setTT = (i, k, v) => {
    const tts = [...form.ticket_types];
    tts[i] = { ...tts[i], [k]: k === "name" ? v : Number(v) };
    setForm({ ...form, ticket_types: tts });
  };
  const removeTT = (i) => setForm({ ...form, ticket_types: form.ticket_types.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.name || !form.ticket_types.length) { toast.error("Nama acara & minimal 1 jenis tiket wajib diisi"); return; }
    setSaving(true);
    try {
      const payload = { ...form, ticket_types: form.ticket_types.map((t) => ({ ...t, price: Number(t.price), quota: Number(t.quota) })) };
      if (editId) await api.put(`/events/${editId}`, payload);
      else await api.post("/events", payload);
      toast.success(editId ? "Acara diperbarui" : "Acara dibuat");
      setOpen(false); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menyimpan");
    }
    setSaving(false);
  };

  const doDelete = async () => {
    try { await api.delete(`/events/${deleteId}`); toast.success("Acara dihapus"); load(); }
    catch { toast.error("Gagal menghapus"); }
    setDeleteId(null);
  };

  return (
    <AdminLayout title="Kelola Acara">
      <div className="flex justify-end mb-4">
        <Button data-testid="new-event-btn" onClick={openNew} className="bg-primary text-black font-bold hover:bg-cyan-300 rounded-none gap-2">
          <Plus size={18} weight="bold" /> Acara Baru
        </Button>
      </div>

      <div className="grid gap-3" data-testid="admin-events-list">
        {events.map((ev) => (
          <div key={ev.id} className="bg-card border border-border/40 p-4 flex items-center gap-4">
            <img src={ev.poster_url} alt="" onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=400&q=80"; }} className="w-20 h-16 object-cover shrink-0 bg-zinc-800" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-heading font-bold text-lg truncate">{ev.name}</h3>
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 shrink-0 ${ev.status === "published" ? "bg-[#00FF66]/15 text-[#00FF66]" : "bg-zinc-700/40 text-zinc-400"}`}>
                  {ev.status === "published" ? "Aktif" : "Nonaktif"}
                </span>
              </div>
              <p className="text-sm text-zinc-500 truncate">{formatDateTime(ev.date)} · {ev.location}</p>
              <div className="flex gap-2 mt-1 flex-wrap">
                {ev.ticket_types.map((t) => (
                  <span key={t.id} className="text-[10px] uppercase tracking-wider bg-white/5 px-2 py-0.5 text-zinc-400">
                    {t.name}: {t.sold}/{t.quota}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button data-testid={`edit-event-${ev.id}`} onClick={() => openEdit(ev)} variant="outline" size="icon" className="border-white/20 rounded-none hover:bg-primary hover:text-black">
                <PencilSimple size={16} />
              </Button>
              <Button data-testid={`delete-event-${ev.id}`} onClick={() => setDeleteId(ev.id)} variant="outline" size="icon" className="border-white/20 rounded-none hover:bg-secondary hover:text-white hover:border-secondary">
                <Trash size={16} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto rounded-none">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editId ? "Edit Acara" : "Acara Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Nama Acara</Label>
              <Input data-testid="event-name-input" value={form.name} onChange={set("name")} className="mt-1 bg-zinc-900 border-zinc-800 rounded-none focus:ring-primary focus:border-primary" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Deskripsi</Label>
              <Textarea data-testid="event-desc-input" value={form.description} onChange={set("description")} className="mt-1 bg-zinc-900 border-zinc-800 rounded-none focus:ring-primary focus:border-primary" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">Waktu Mulai</Label>
                <Input data-testid="event-date-input" type="datetime-local" value={form.date} onChange={set("date")} className="mt-1 bg-zinc-900 border-zinc-800 rounded-none focus:ring-primary focus:border-primary [color-scheme:dark]" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">Waktu Berakhir</Label>
                <Input data-testid="event-end-date-input" type="datetime-local" value={form.end_date || ""} onChange={set("end_date")} className="mt-1 bg-zinc-900 border-zinc-800 rounded-none focus:ring-primary focus:border-primary [color-scheme:dark]" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Kategori</Label>
              <Input data-testid="event-category-input" value={form.category} onChange={set("category")} className="mt-1 bg-zinc-900 border-zinc-800 rounded-none focus:ring-primary focus:border-primary" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Lokasi</Label>
              <Input data-testid="event-location-input" value={form.location} onChange={set("location")} className="mt-1 bg-zinc-900 border-zinc-800 rounded-none focus:ring-primary focus:border-primary" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">URL Poster</Label>
              <Input data-testid="event-poster-input" value={form.poster_url} onChange={set("poster_url")} placeholder="https://... (URL gambar / Google Drive / Dropbox)" className="mt-1 bg-zinc-900 border-zinc-800 rounded-none focus:ring-primary focus:border-primary" />
              <p className="text-[11px] text-zinc-600 mt-1">Bisa tempel link Google Drive, Dropbox, atau URL gambar langsung — otomatis dikonversi.</p>
            </div>
            <div className="flex items-center justify-between border border-white/10 p-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-300">Status Acara</Label>
                <p className="text-[11px] text-zinc-500 mt-0.5">{form.status === "published" ? "Aktif — tampil & bisa dibeli" : "Nonaktif — disembunyikan dari publik"}</p>
              </div>
              <Switch data-testid="event-active-switch" checked={form.status === "published"}
                onCheckedChange={(v) => setForm({ ...form, status: v ? "published" : "draft" })}
                className="data-[state=checked]:bg-primary" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-widest text-primary">Jenis Tiket</Label>
                <Button data-testid="add-ticket-type-btn" onClick={addTT} size="sm" variant="outline" className="rounded-none border-white/20 h-7 gap-1"><Plus size={14} /> Tambah</Button>
              </div>
              <div className="space-y-2">
                {form.ticket_types.map((t, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input data-testid={`tt-name-${i}`} placeholder="Nama" value={t.name} onChange={(e) => setTT(i, "name", e.target.value)} className="bg-zinc-900 border-zinc-800 rounded-none focus:ring-primary focus:border-primary" />
                    <Input data-testid={`tt-price-${i}`} type="number" placeholder="Harga" value={t.price} onChange={(e) => setTT(i, "price", e.target.value)} className="bg-zinc-900 border-zinc-800 rounded-none w-32 focus:ring-primary focus:border-primary" />
                    <Input data-testid={`tt-quota-${i}`} type="number" placeholder="Kuota" value={t.quota} onChange={(e) => setTT(i, "quota", e.target.value)} className="bg-zinc-900 border-zinc-800 rounded-none w-24 focus:ring-primary focus:border-primary" />
                    <button data-testid={`tt-remove-${i}`} onClick={() => removeTT(i)} className="text-zinc-500 hover:text-secondary shrink-0"><X size={18} /></button>
                  </div>
                ))}
              </div>
            </div>

            <Button data-testid="save-event-btn" onClick={save} disabled={saving} className="w-full bg-primary text-black font-bold hover:bg-cyan-300 rounded-none h-11">
              {saving ? "Menyimpan..." : "Simpan Acara"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-card border-white/10 rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus acara ini?</AlertDialogTitle>
            <AlertDialogDescription>Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-white/20">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={doDelete} className="bg-secondary text-white hover:bg-secondary/80 rounded-none">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
